import type { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useUserContext } from '@/lib/auth/useUserContext';

interface Props {
  /** Authorization check. Receives the current groups; return true to allow. */
  predicate: (groups: readonly string[]) => boolean;
  /** Where to send users who fail the check. */
  fallback?: string;
  children: ReactNode;
}

/**
 * Like RequireAnyGroup but takes an arbitrary predicate over the user's groups,
 * so we can express patterns like "any TMKN-Qargo-Mgr-* group" without
 * enumerating every store's group name. Must be nested inside <RequireAuth>.
 */
export function RequirePredicate({
  predicate,
  fallback = '/dashboard',
  children,
}: Props): ReactNode {
  const { groups, loading, isAuthenticated } = useUserContext();

  if (!isAuthenticated) {
    return <Navigate to="/signin" replace />;
  }
  if (loading && groups.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center text-muted-foreground">
        Checking permissions…
      </div>
    );
  }
  if (!predicate(groups)) {
    return <Navigate to={fallback} replace />;
  }
  return <>{children}</>;
}
